import { Injectable } from '@angular/core';
import { captureException } from '@sentry/angular';

/**
 * Enum defining all dynamically loadable module keys.
 * Add new modules here to make them available for dynamic loading.
 */
export enum DynamicModuleKey {
  KRISP_NOISE_FILTER = '@livekit/krisp-noise-filter',
  TENSORFLOW_HAND_POSE_DETECTION = '@tensorflow-models/hand-pose-detection',
  MEDIAPIPE_HANDS = '@mediapipe/hands',
  TENSORFLOW_BACKEND_WEBGL = '@tensorflow/tfjs-backend-webgl',
}

/**
 * Centralized registry of all dynamically loadable modules.
 * Maps enum keys to their dynamic import functions.
 */
const MODULE_REGISTRY: Record<DynamicModuleKey, () => Promise<any>> = {
  [DynamicModuleKey.KRISP_NOISE_FILTER]: () => import('@livekit/krisp-noise-filter'),
  [DynamicModuleKey.TENSORFLOW_HAND_POSE_DETECTION]: () =>
    import('@tensorflow-models/hand-pose-detection'),
  [DynamicModuleKey.MEDIAPIPE_HANDS]: () => import('@mediapipe/hands'),
  [DynamicModuleKey.TENSORFLOW_BACKEND_WEBGL]: () => import('@tensorflow/tfjs-backend-webgl'),
};

/**
 * Type helper to infer the loaded module type from a registry key.
 */
type ModuleType<K extends DynamicModuleKey> = K extends DynamicModuleKey
  ? Awaited<ReturnType<(typeof MODULE_REGISTRY)[K]>>
  : never;

/**
 * Options for loading a dynamic module.
 */
export interface DynamicModuleLoadOptions {
  /**
   * If true, errors are logged but not thrown. Module load returns null on failure.
   * If false, errors are thrown and must be handled by the caller.
   * @default true
   */
  silent?: boolean;

  /**
   * If true, the loaded module is cached in memory for future use.
   * If false, the module is loaded fresh each time.
   * @default true
   */
  cacheable?: boolean;
}

/**
 * Internal state for tracking module loading.
 */
interface ModuleState<T = any> {
  value: T | null;
  loadingPromise: Promise<T | null> | null;
  loadedAt: number | null;
  failed: boolean;
}

/**
 * Service for dynamically loading heavy third-party packages on-demand.
 *
 * This service provides a centralized, scalable approach to lazy-loading
 * large dependencies, reducing initial bundle size and improving performance.
 *
 * Features:
 * - In-memory caching of loaded modules
 * - Prevents duplicate simultaneous loads
 * - Configurable error handling (silent vs throwing)
 * - Type-safe module loading
 * - Extensible for future packages
 *
 * @example
 * ```typescript
 * const krispModule = await this.dynamicModuleLoader.loadModule(
 *   DynamicModuleKey.KRISP_NOISE_FILTER,
 *   { silent: true }
 * );
 * if (krispModule) {
 *   const processor = krispModule.KrispNoiseFilter();
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class DynamicModuleLoaderService {
  private moduleCache = new Map<DynamicModuleKey, ModuleState>();

  /**
   * Loads a module dynamically with caching and error handling.
   *
   * @template K The module key from the registry
   * @param moduleKey The registered module key to load
   * @param options Loading options (silent, cacheable)
   * @returns Promise resolving to the loaded module or null on failure (if silent)
   */
  async loadModule<K extends DynamicModuleKey>(
    moduleKey: K,
    options: DynamicModuleLoadOptions = {},
  ): Promise<ModuleType<K> | null> {
    const { silent = true, cacheable = true } = options;
    const loader = MODULE_REGISTRY[moduleKey];

    if (!loader) {
      const error = `Module key "${moduleKey}" not found in registry`;
      console.error(`[DynamicModuleLoader] ${error}`);
      captureException(new Error(error));
      return null;
    }

    // Check if module is already cached
    const cached = this.moduleCache.get(moduleKey);
    if (cached) {
      // Return cached module if already loaded
      if (cached.value !== null) {
        return cached.value as ModuleType<K>;
      }

      // If currently loading, wait for the existing promise
      if (cached.loadingPromise !== null) {
        return cached.loadingPromise as Promise<ModuleType<K> | null>;
      }

      // Return null if previous load failed and we're in silent mode
      if (cached.failed && silent) {
        console.warn(`[DynamicModuleLoader] Module previously failed: ${moduleKey}`);
        return null;
      }
    }

    // Initialize module state
    const state: ModuleState = {
      value: null,
      loadingPromise: null,
      loadedAt: null,
      failed: false,
    };

    // Create loading promise
    const loadingPromise = this.performLoad(moduleKey, loader as () => Promise<any>, state, silent);
    state.loadingPromise = loadingPromise;

    // Cache the state if cacheable
    if (cacheable) {
      this.moduleCache.set(moduleKey, state);
    }

    return loadingPromise as Promise<ModuleType<K> | null>;
  }

  /**
   * Internal method to perform the actual module loading.
   */
  private async performLoad<T>(
    moduleKey: DynamicModuleKey,
    loader: () => Promise<T>,
    state: ModuleState<T>,
    silent: boolean,
  ): Promise<T | null> {
    const startTime = performance.now();

    try {
      const module = await loader();

      // Update state with loaded module
      state.value = module;
      state.loadedAt = Date.now();
      state.failed = false;
      // Keep loadingPromise for future reference

      return module;
    } catch (error) {
      const loadTime = Math.round(performance.now() - startTime);
      console.error(
        `[DynamicModuleLoader] Failed to load module: ${moduleKey} (${loadTime}ms)`,
        error,
      );

      // Update state to indicate failure
      state.failed = true;

      // Log to error tracking
      captureException(new Error(`Failed to load dynamic module: ${moduleKey}`), {
        extra: {
          moduleKey,
          error: error instanceof Error ? error.message : String(error),
          loadTime,
        },
      });

      if (silent) {
        return null;
      } else {
        throw error;
      }
    }
  }

  /**
   * Checks if a module is already loaded and cached.
   *
   * @param moduleKey The module to check
   * @returns True if the module is loaded and available in cache
   */
  isModuleLoaded(moduleKey: DynamicModuleKey): boolean {
    const cached = this.moduleCache.get(moduleKey);
    return !!cached && cached.value !== null;
  }

  /**
   * Gets a cached module without loading it.
   *
   * @template K The module key from the registry
   * @param moduleKey The module to retrieve
   * @returns The cached module or null if not loaded
   */
  getCachedModule<K extends DynamicModuleKey>(moduleKey: K): ModuleType<K> | null {
    const cached = this.moduleCache.get(moduleKey);
    return (cached?.value as ModuleType<K>) || null;
  }

}
